
import { Badge, Button, Checkbox, Grid, Group, NumberFormatter, NumberInput, Select, SimpleGrid, Stack, Text, ThemeIcon, Tooltip, } from '@mantine/core';
import { HitsPerPage, InstantSearch, Pagination, SearchBox, useHits, InstantSearchProps, Configure, useRefinementList, usePagination, useSortBy } from 'react-instantsearch';
import { DatePickerInput } from '@mantine/dates';

import { TagBadge } from './tag_badge';
import FilterModal, { CustomFilterSection, FilterSection } from './FilterModal';
import { FilterBadgeGroup } from './FilterBadges';
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MyUserDataContext } from '../contexts';
import DataTable, { TableColumn } from 'react-data-table-component';

import { useSessionStorage } from '@mantine/hooks';
import { Order, OrderStatusCode, } from '../types';
import { Timestamp } from 'firebase/firestore';
import { dateFormater } from '../helpers/common';

import { IconEye, IconMoneybag, IconReceipt, IconReceiptRefund, IconWriting } from '@tabler/icons-react';
import { CurrentViewers } from './current_viewers';

//typesense

interface InitialState {
    query?: string;
    page?: number;
    refinementList?: Record<string, string[]>;
    sortBy?: string;
    hitsPerPage?: number;
}

export interface OrdersSummaryFilterSelection {
    fundName: string;
    orderStatusCode: string;
    expectedNavDate: string;
}

export interface OrdersSummaryFilterRequest extends OrdersSummaryFilterSelection {
    requestId: number;
}

type OrderDateFilterField = 'created' | 'approvedAt' | 'expected_nav_date' | 'transferDate';

interface OrderDateFilterState {
    field: OrderDateFilterField;
    from: string | null;
    to: string | null;
}

type OrderNumericFilterField = 'transferAmount' | 'units';

interface OrderNumericRange {
    min: string | null;
    max: string | null;
}

type OrderNumericFilterState = Record<OrderNumericFilterField, OrderNumericRange>;

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DATE_FILTER_FALLBACK_STATE: OrderDateFilterState = {
    field: 'created',
    from: null,
    to: null,
};
const ORDER_DATE_FILTER_OPTIONS: Array<{ value: OrderDateFilterField; label: string }> = [
    { value: 'created', label: 'Created date' },
    { value: 'approvedAt', label: 'Approval date' },
    { value: 'expected_nav_date', label: 'Expected NAV date' },
    { value: 'transferDate', label: 'Transfer date' },
];
const ORDER_DATE_FILTER_LABELS: Record<OrderDateFilterField, string> = {
    created: 'Created date',
    approvedAt: 'Approval date',
    expected_nav_date: 'Expected NAV date',
    transferDate: 'Transfer date',
};

const NUMERIC_FILTER_FALLBACK_STATE: OrderNumericFilterState = {
    transferAmount: { min: null, max: null },
    units: { min: null, max: null },
};
const ORDER_NUMERIC_FILTER_OPTIONS: Array<{ field: OrderNumericFilterField; label: string }> = [
    { field: 'transferAmount', label: 'Transfer amount' },
    { field: 'units', label: 'Units' },
];
const ORDER_NUMERIC_FILTER_LABELS: Record<OrderNumericFilterField, string> = {
    transferAmount: 'Transfer amount',
    units: 'Units',
};

const toStartOfDay = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

const buildLocalDateFromString = (value: string): Date | null => {
    const [year, month, day] = value.split('-').map(Number);

    if (!year || !month || !day) {
        return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const toDateInputString = (value: Date | string | null) => {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        return value;
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const toUnixTimestampSeconds = (value: string, endExclusive = false) => {
    const date = buildLocalDateFromString(value);

    if (!date) {
        return null;
    }

    if (endExclusive) {
        date.setDate(date.getDate() + 1);
    }

    return Math.floor(date.getTime() / 1000);
};

const buildOrderDateFilter = (value: OrderDateFilterState) => {
    const fromValue = value.from ? toUnixTimestampSeconds(value.from) : null;
    const toValue = value.to ? toUnixTimestampSeconds(value.to, true) : null;

    if (fromValue === null && toValue === null) {
        return null;
    }

    const filters = [
        fromValue !== null ? `${value.field}:>=${fromValue}` : null,
        toValue !== null ? `${value.field}:<${toValue}` : null,
    ].filter(Boolean);

    return filters.length ? filters.join(' && ') : null;
};

const formatDateFilterSummary = (value: OrderDateFilterState) => {
    const fromDate = value.from ? buildLocalDateFromString(value.from) : null;
    const toDate = value.to ? buildLocalDateFromString(value.to) : null;

    if (!fromDate && !toDate) {
        return null;
    }

    const dateLabel = ORDER_DATE_FILTER_LABELS[value.field];

    if (fromDate && toDate) {
        return `${dateLabel}: ${dateFormater(fromDate)} to ${dateFormater(toDate)}`;
    }

    if (fromDate) {
        return `${dateLabel}: from ${dateFormater(fromDate)}`;
    }

    return `${dateLabel}: until ${dateFormater(toDate!)}`;
};

const parseNumericValue = (value: string | null): number | null => {
    if (value === null) {
        return null;
    }

    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }

    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
};

const buildOrderNumericFilter = (state: OrderNumericFilterState) => {
    const filters = ORDER_NUMERIC_FILTER_OPTIONS.flatMap(({ field }) => {
        const minValue = parseNumericValue(state[field].min);
        const maxValue = parseNumericValue(state[field].max);

        return [
            minValue !== null ? `${field}:>=${minValue}` : null,
            maxValue !== null ? `${field}:<=${maxValue}` : null,
        ].filter(Boolean) as string[];
    });

    return filters.length ? filters.join(' && ') : null;
};

const numberFormatter = new Intl.NumberFormat();

const formatNumericFilterSummaries = (state: OrderNumericFilterState) => {
    return ORDER_NUMERIC_FILTER_OPTIONS.flatMap(({ field }) => {
        const minValue = parseNumericValue(state[field].min);
        const maxValue = parseNumericValue(state[field].max);

        if (minValue === null && maxValue === null) {
            return [];
        }

        const label = ORDER_NUMERIC_FILTER_LABELS[field];

        if (minValue !== null && maxValue !== null) {
            return [`${label}: ${numberFormatter.format(minValue)} to ${numberFormatter.format(maxValue)}`];
        }

        if (minValue !== null) {
            return [`${label}: from ${numberFormatter.format(minValue)}`];
        }

        return [`${label}: up to ${numberFormatter.format(maxValue!)}`];
    });
};

const toSearchDate = (value: unknown): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Timestamp) {
        return value.toDate();
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const parsedDate = new Date((value > 1_000_000_000_000 ? value : value * 1000));
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "object" && value !== null) {
        if ("toDate" in value && typeof value.toDate === "function") {
            const parsedDate = value.toDate();
            return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
        }

        if ("seconds" in value && typeof value.seconds === "number") {
            const parsedDate = new Date(value.seconds * 1000);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }
    }

    return null;
};

const getExpectedNavBadgeState = (expectedNavDate: Date) => {
    const today = toStartOfDay(new Date());
    const targetDate = toStartOfDay(expectedNavDate);
    const dayDiff = Math.round((targetDate.getTime() - today.getTime()) / DAY_IN_MS);

    if (dayDiff < 0) {
        return {
            color: "red",
            tooltip: `Overdue by ${Math.abs(dayDiff)} day(s)`,
        };
    }

    if (dayDiff === 0) {
        return {
            color: "orange",
            tooltip: "Expected today",
        };
    }

    if (dayDiff === 1) {
        return {
            color: "yellow",
            tooltip: "Expected tomorrow",
        };
    }

    return {
        color: "green",
        tooltip: `Expected in ${dayDiff} day(s)`,
    };
};

const TableDisplay = ({
    setSelectedRows,
    selectedRows,
    renderTags,
    ActionsComponent,
    navigateDetails,
    indexName,
    dateFilterState,
    onDateFilterChange,
    onDateFilterClear,
    numericFilterState,
    onNumericFilterChange,
    onNumericFilterClear,
    summaryFilterRequest,
    ...props
}: any) => {
    const { items
        // ,results
    } = useHits();
    const createdDescSortValue = `${indexName}/sort/created:desc`;

    // const facets_list = Object.keys((results as any)?._rawResults[0]?.facets);// this returns an object with keys of facets and their count, maybe we can use it to list all avaiable facets and trigger a refine accordigly
    // console.log("results=========>>", results ? (results as any)?._rawResults[0]?.facets : "NO RESULTS");
    const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
    const { currentRefinement: currentSortBy, refine: refineSortBy } = useSortBy({
        items: [{ label: 'Created (desc)', value: createdDescSortValue }],
    });
    const {
        items: fundsItems,
        refine: refineFunds,

    } = useRefinementList({ ...props, attribute: "fund.name_en", operator: "or", limit: 9999 });


    const {
        items: orderTypeItems,
        refine: refineOrderTypes,

    } = useRefinementList({ ...props, attribute: "type", operator: "or", limit: 9999 });

    const {
        items: orderStatusItems,
        refine: refineOrderStatus,

    } = useRefinementList({ ...props, attribute: "tags.orderStatus.code", operator: "or", limit: 9999 });

    const {
        items: kycStatusItems,
        refine: refineKYCStatus,

    } = useRefinementList({ ...props, attribute: "tags.kycStatus.code", operator: "or", limit: 9999 });
    const { refine: refinePage } = usePagination();
    const lastAppliedSummaryFilterRequestRef = useRef<number | null>(null);

    const dateFilterSummary = useMemo(
        () => formatDateFilterSummary(dateFilterState),
        [dateFilterState]
    );
    const numericFilterSummaries = useMemo(
        () => formatNumericFilterSummaries(numericFilterState),
        [numericFilterState]
    );

    useEffect(() => {
        if (currentSortBy !== createdDescSortValue) {
            refineSortBy(createdDescSortValue);
        }
    }, [createdDescSortValue, currentSortBy, refineSortBy]);

    useEffect(() => {
        if (!summaryFilterRequest || summaryFilterRequest.requestId === lastAppliedSummaryFilterRequestRef.current) {
            return;
        }

        lastAppliedSummaryFilterRequestRef.current = summaryFilterRequest.requestId;
        onDateFilterChange({
            field: 'expected_nav_date',
            from: summaryFilterRequest.expectedNavDate,
            to: summaryFilterRequest.expectedNavDate,
        });

        fundsItems
            .filter((item) => item.isRefined && item.value !== summaryFilterRequest.fundName)
            .forEach((item) => refineFunds(item.value));

        if (!fundsItems.some((item) => item.isRefined && item.value === summaryFilterRequest.fundName)) {
            refineFunds(summaryFilterRequest.fundName);
        }

        orderStatusItems
            .filter((item) => item.isRefined && item.value !== summaryFilterRequest.orderStatusCode)
            .forEach((item) => refineOrderStatus(item.value));

        if (!orderStatusItems.some((item) => item.isRefined && item.value === summaryFilterRequest.orderStatusCode)) {
            refineOrderStatus(summaryFilterRequest.orderStatusCode);
        }

        refinePage(0);
    }, [
        fundsItems,
        onDateFilterChange,
        orderStatusItems,
        refineFunds,
        refineOrderStatus,
        refinePage,
        summaryFilterRequest,
    ]);
    const dateFilterSection = useMemo(() => {
        const fromDate = dateFilterState.from ? buildLocalDateFromString(dateFilterState.from) : null;
        const toDate = dateFilterState.to ? buildLocalDateFromString(dateFilterState.to) : null;

        return {
            id: 'date',
            title: 'Filter by Date',
            type: 'custom',
            selectedCount: dateFilterSummary ? 1 : 0,
            onClear: onDateFilterClear,
            renderContent: () => (
                <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                        Choose which order date to filter, then set a start and end date.
                    </Text>
                    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm" verticalSpacing="sm">
                        <Select
                            label="Date field"
                            data={ORDER_DATE_FILTER_OPTIONS}
                            value={dateFilterState.field}
                            onChange={(value) => {
                                if (!value) {
                                    return;
                                }

                                onDateFilterChange({
                                    ...dateFilterState,
                                    field: value as OrderDateFilterField,
                                });
                            }}
                            allowDeselect={false}
                        />
                        <DatePickerInput
                            label="From"
                            placeholder="Start date"
                            value={fromDate}
                            valueFormat="DD MMM YYYY"
                            clearable
                            maxDate={toDate ?? undefined}
                            onChange={(value) =>
                                onDateFilterChange({
                                    ...dateFilterState,
                                    from: toDateInputString(value),
                                })
                            }
                        />
                        <DatePickerInput
                            label="To"
                            placeholder="End date"
                            value={toDate}
                            valueFormat="DD MMM YYYY"
                            clearable
                            minDate={fromDate ?? undefined}
                            onChange={(value) =>
                                onDateFilterChange({
                                    ...dateFilterState,
                                    to: toDateInputString(value),
                                })
                            }
                        />
                    </SimpleGrid>
                </Stack>
            ),
        } satisfies CustomFilterSection;
    }, [dateFilterState, dateFilterSummary, onDateFilterChange, onDateFilterClear]);

    const numericFilterSection = useMemo(() => {
        const handleRangeChange = (
            field: OrderNumericFilterField,
            bound: keyof OrderNumericRange,
            value: number | string
        ) => {
            const nextValue = value === '' || value === null || value === undefined
                ? null
                : String(value);

            onNumericFilterChange({
                ...numericFilterState,
                [field]: {
                    ...numericFilterState[field],
                    [bound]: nextValue,
                },
            });
        };

        return {
            id: 'numeric',
            title: 'Filter by Amount / Units',
            type: 'custom',
            selectedCount: numericFilterSummaries.length,
            onClear: onNumericFilterClear,
            renderContent: () => (
                <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                        Set a minimum and/or maximum value for transfer amount and units.
                    </Text>
                    {ORDER_NUMERIC_FILTER_OPTIONS.map(({ field, label }) => (
                        <SimpleGrid key={field} cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                            <NumberInput
                                label={`${label} (min)`}
                                placeholder="Min"
                                value={numericFilterState[field].min ?? ''}
                                thousandSeparator
                                allowNegative={false}
                                onChange={(value) => handleRangeChange(field, 'min', value)}
                            />
                            <NumberInput
                                label={`${label} (max)`}
                                placeholder="Max"
                                value={numericFilterState[field].max ?? ''}
                                thousandSeparator
                                allowNegative={false}
                                onChange={(value) => handleRangeChange(field, 'max', value)}
                            />
                        </SimpleGrid>
                    ))}
                </Stack>
            ),
        } satisfies CustomFilterSection;
    }, [numericFilterState, numericFilterSummaries, onNumericFilterChange, onNumericFilterClear]);


    const handleChange = ({ selectedRows }: any) => {
        console.log("selectedRows  =====>>>", selectedRows);
        // You can set state or dispatch with something like Redux so we can use the retrieved data
        if (setSelectedRows) {
            setSelectedRows(selectedRows);
        }
    };


    const columns: TableColumn<Order>[] = [

        {
            // name: "Type",
            cell: (row: Order) => {

                let icons = [];

                if (row.type === "subscription") icons.push(
                    <Tooltip label="Subscription" transitionProps={{ transition: 'scale', duration: 500 }}>
                        <ThemeIcon variant="white" color="green">
                            <IconReceipt size={16} />
                        </ThemeIcon>
                    </Tooltip>
                )
                else if (row.type === "redemption") icons.push(
                    <Tooltip label="Redemption" transitionProps={{ transition: 'scale', duration: 500 }}>
                        <ThemeIcon variant="white" color="orange">
                            <IconReceiptRefund size={16} />
                        </ThemeIcon>
                    </Tooltip>
                )

                if (row.tags?.paid) icons.push(

                    <Tooltip label="Paid" transitionProps={{ transition: 'scale', duration: 500 }}>
                        <ThemeIcon variant="white" color="yellow">
                            <IconMoneybag size={16} />
                        </ThemeIcon>
                    </Tooltip>
                )

                if (row.tags?.manual) icons.push(
                    <Tooltip label="Manual" transitionProps={{ transition: 'scale', duration: 500 }}>
                        <ThemeIcon variant="white" color="blue">
                            <IconWriting size={16} />
                        </ThemeIcon>
                    </Tooltip>

                )

                return (<>{icons}</>)
            },
            // sortable: true,
            width: "90px",
        },
        {
            name: <Tooltip label="Current Viewers" transitionProps={{ transition: 'scale', duration: 500 }}><IconEye size={16} /></Tooltip>,
            cell(row,) {

                return (
                    <CurrentViewers type="orders" id={row.id!} />
                );
            },
            // sortable: true,
            width: "90px",
        },
        {
            name: "Name",
            id: "customerObj.name_en",
            selector: (row: Order) => {

                return row.customerObj?.name_en ?? ""
            },
            grow: 1,

        },
        {
            name: "Mobile",
            selector: (row: Order) => row.customerObj?.mobile ?? "",

            // sortable: true,
            grow: 1,



        },
        {
            name: "Civil ID / Commercial register",
            id: "customerObj.civilID",
            selector: (row: Order) => (row.customerObj?.civilID || row.customerObj?.commercial_register) ?? "",
            grow: 1,

        },

        {
            name: "Fund",
            id: "fund.name_en",

            selector: (row: Order) => row?.fund?.name_en,
        },
        {
            name: "Date",
            id: "created",
            cell: (row: Order) => {
                const created = toSearchDate(row.created);

                if (!created) {
                    return row.created ? String(row.created) : "-";
                }

                return created.toLocaleString("en-GB", { hour12: true });
            },

        },
        {
            name: "Expected NAV",
            cell: (row: Order) => {
                const expectedNavDate = toSearchDate(row.expected_nav_date);

                if (!expectedNavDate) {
                    return (
                        <Text size="sm" c="dimmed">
                            -
                        </Text>
                    );
                }

                const badgeState = getExpectedNavBadgeState(expectedNavDate);

                return (
                    <Tooltip label={badgeState.tooltip} transitionProps={{ transition: 'scale', duration: 500 }}>
                        <Badge size="sm" variant="light" color={badgeState.color}>
                            {dateFormater(expectedNavDate)}
                        </Badge>
                    </Tooltip>
                );
            },
            grow: 1,
        },

        {
            name: "Units",

            cell: (row: Order) => {
                return Number(row.units) === 0 ?
                    <Text size="sm" ta="right">
                        All Units
                    </Text> as any
                    :

                    <NumberFormatter value={Number(row.units)} thousandSeparator />
            },

            grow: 1,

        },
        {
            name: "Tags",
            // wrap: true,
            // width: "150px",
            cell: (row: Order) => {
                if (!row.tags) return;

                return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    <TagBadge m={3} tag={row.tags["orderStatus"]} key="orderStatus" />
                    {row.tags["kycStatus"] && <TagBadge m={3} tag={row.tags["kycStatus"]} key="kycStatus" />}
                </div>

                /*
                                //loop through tags and create a badge for each
                                let tagsList: React.JSX.Element[] = [];
                                for (let tagKey of Object.keys(row.tags!).sort()) {
                                    let tag = row.tags![tagKey];
                
                                    // tagsList.push(<Badge size="xs" variant="filled" c={tag.bgColor} key={tagKey} style={{ backgroundColor: tag.bgColor, color: tag.color }}>{tag.title}</Badge>);
                                    tagsList.push(<TagBadge tag={tag} key={tagKey} />);
                                    // tagsList.push(" ");
                                }
                
                                return <>{tagsList}</> as any;
                */
            },
        },

        {
            name: "",
            cell: (row: any) => <>

                {ActionsComponent && <ActionsComponent orderObj={row} />}
            </>,

            grow: 0,

        }


    ];



    return <Grid justify='center' align='center'>


        <Grid.Col span={12}  >
            <Group my="sm">

                <SearchBox

                    style={{
                        display: "flex",
                        justifyContent: "flex-end",

                        width: "90%",
                    }}
                    autoFocus
                    placeholder='Search...'


                />


                <FilterModal
                    opened={openFilterModal}
                    onClose={() => setOpenFilterModal(false)}
                    sections={[
                        dateFilterSection,
                        {
                            id: 'funds',
                            title: 'Filter by Funds',
                            items: fundsItems,
                            refine: refineFunds,
                        },
                        {
                            id: 'type',
                            title: 'Filter by Order Type',
                            items: orderTypeItems,
                            refine: refineOrderTypes,
                        },
                        {
                            id: 'status',
                            title: 'Filter by Order Status',
                            items: orderStatusItems,
                            refine: refineOrderStatus,
                        },
                        {
                            id: 'kyc_status',
                            title: 'Filter by KYC Status',
                            items: kycStatusItems,
                            refine: refineKYCStatus,
                        },
                        numericFilterSection,
                    ] as FilterSection[]}
                />
                <Button onClick={() => setOpenFilterModal(true)}>
                    Filter
                </Button>
            </Group>

            <FilterBadgeGroup
                items={fundsItems}
                color="grape"
                refine={refineFunds}
            />
            <FilterBadgeGroup
                items={orderTypeItems}
                color="cyan"
                refine={refineOrderTypes}
            />
            <FilterBadgeGroup
                items={orderStatusItems}
                color="red"
                refine={refineOrderStatus}
            />
            <FilterBadgeGroup
                items={kycStatusItems}
                color="teal"
                refine={refineKYCStatus}
            />
            {dateFilterSummary ? (
                <Group my="sm">
                    <Badge color="indigo">
                        {dateFilterSummary}
                        <Button
                            variant="subtle"
                            c="white"
                            onClick={onDateFilterClear}
                        >
                            X
                        </Button>
                    </Badge>
                </Group>
            ) : null}
            {numericFilterSummaries.length ? (
                <Group my="sm">
                    {numericFilterSummaries.map((summary: string) => (
                        <Badge key={summary} color="indigo">
                            {summary}
                            <Button
                                variant="subtle"
                                c="white"
                                onClick={onNumericFilterClear}
                            >
                                X
                            </Button>
                        </Badge>
                    ))}
                </Group>
            ) : null}

            <DataTable
                columns={columns}
                selectableRows={selectedRows}

                onSelectedRowsChange={handleChange}
                data={items.map((hitObj: any) => {

                    return {
                        ...hitObj,
                        customerID: hitObj?.customerObj?.civilID,
                    }
                })}
            />
        </Grid.Col>
    </Grid>
}

interface OrdersInstantSearchProps {
    indexName: string,
    setSelectedRows?: any
    renderTags?: boolean
    ActionsComponent?: any
    navigateDetails?: any
    selectable?: boolean
    selectedRows?: any[]
    defaultFilter?: string
    allowShowAll?: boolean
    summaryFilterRequest?: OrdersSummaryFilterRequest | null

    topElements?: JSX.Element
    additionalChildren?: JSX.Element
}
const OrdersInstantSearch = ({ indexName, selectable, setSelectedRows, selectedRows = [], navigateDetails, renderTags, ActionsComponent, topElements, additionalChildren, defaultFilter = "", allowShowAll = true, summaryFilterRequest = null }: OrdersInstantSearchProps) => {

    const [orderStatusFilter, setOrderStatusFilter] = useState<string | null>()
    const userData = useContext(MyUserDataContext);
    let customClaims = userData.firebaseTokenResult?.claims.customClaims as any;

    const handleRowsToShow = () => {


        const orderStatuses: string[] = []

        if (customClaims["rm"] === true || customClaims["kfh_rm"] === true || customClaims["kfh_oper"] === true) {
            orderStatuses.push(...[OrderStatusCode.need_slip, OrderStatusCode.need_signature, OrderStatusCode.signature_verification, OrderStatusCode.need_form])

        }
        if (customClaims["cr"] === true) {
            orderStatuses.push(...[OrderStatusCode.review, OrderStatusCode.approval, OrderStatusCode.waiting_for_kyc, OrderStatusCode.signature_verification])


        }
        if (customClaims["oper"] === true) {
            orderStatuses.push(...[OrderStatusCode.processing, OrderStatusCode.waiting_for_nav])

        }

        if (orderStatuses.length) {



            setOrderStatusFilter(`tags.orderStatus.code:[${orderStatuses.join()}]`)

        }
        else {
            setOrderStatusFilter(null)
        }

    }
    const typesenseSearchClient = useMemo(() => new TypesenseInstantSearchAdapter({
        server: {
            apiKey: userData.customSearchKey!, // Be sure to use a Search API Key
            nodes: [
                {
                    host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
                    port: 443,
                    protocol: 'https'
                },
            ],
        },
        additionalSearchParameters: {

            query_by: "customerObj,fund,tags,rm.name_en,rm.name_ar,rm.rm_id,referredBy.email",
            facet_by: "*"
        }

    }).searchClient, [userData.customSearchKey]);

    if (!indexName) {
        indexName = import.meta.env.VITE_TYPESENSE_ORDERS_COLLECTION!;
    }

    const searchStateScope = `${userData.user?.uid ?? 'anon'}_${(userData.customSearchKey ?? '').slice(0, 12)}`;

    //save to local session
    const [initialUIStateForIndex, setInitialUIStateForIndex] = useSessionStorage<InitialState>({
        key: `initialUIStateFor_${indexName}_${searchStateScope}`,
        getInitialValueInEffect: false,
    });
    const [dateFilterState, setDateFilterState] = useSessionStorage<OrderDateFilterState>({
        key: `ordersDateFilter_${indexName}_${searchStateScope}`,
        defaultValue: DATE_FILTER_FALLBACK_STATE,
        getInitialValueInEffect: false,
    });
    const [numericFilterState, setNumericFilterState] = useSessionStorage<OrderNumericFilterState>({
        key: `ordersNumericFilter_${indexName}_${searchStateScope}`,
        defaultValue: NUMERIC_FILTER_FALLBACK_STATE,
        getInitialValueInEffect: false,
    });


    const onStateChange: InstantSearchProps['onStateChange'] = ({
        uiState,

        setUiState,

    }) => {
        setUiState(uiState);
        if (import.meta.env.MODE !== 'production') {
            console.log("setting ui state", uiState);
        }
        setInitialUIStateForIndex({
            ...(uiState[indexName] ?? {}),
            sortBy: `${indexName}/sort/created:desc`,
        });
    };
    const normalizedDefaultFilter = defaultFilter
        ?.trim()
        .replace(/^(AND|&&)\s+/i, "");
    const initialIndexUiState = useMemo(() => ({
        ...(initialUIStateForIndex ?? {}),
        sortBy: `${indexName}/sort/created:desc`,
    }), [indexName, initialUIStateForIndex]);
    const dateFilterExpression = useMemo(
        () => buildOrderDateFilter(dateFilterState),
        [dateFilterState]
    );
    const numericFilterExpression = useMemo(
        () => buildOrderNumericFilter(numericFilterState),
        [numericFilterState]
    );
    const combinedFilters = useMemo(() => {
        return [orderStatusFilter, normalizedDefaultFilter, dateFilterExpression, numericFilterExpression]
            .filter(Boolean)
            .map((value) => value!.includes(' || ') ? `(${value})` : value!)
            .join(' && ');
    }, [dateFilterExpression, normalizedDefaultFilter, numericFilterExpression, orderStatusFilter]);

    // console.log("session for ", indexName, initialUIStateForIndex);

    // if (!initialUIStateForIndex)  {
    //     return <div></div>;
    // }


    return (
        <InstantSearch

            searchClient={typesenseSearchClient}
            indexName={indexName}
            initialUiState={{
                [indexName]: initialIndexUiState
            }}
            onStateChange={onStateChange}


        >
            <Configure

                filters={combinedFilters}

                exactOnSingleWordQuery='attribute'
            />

            <Group justify="space-between" >

                {topElements}



                {allowShowAll && <Checkbox
                    label="Show all orders"
                    defaultChecked={true}
                    onChange={(event) => {
                        if (!event.currentTarget.checked) {
                            handleRowsToShow()
                        }
                        else {
                            setOrderStatusFilter(null)
                        }
                    }

                    }
                />}




                {additionalChildren}

            </Group>

            <TableDisplay
                indexName={indexName}
                queryValue={initialUIStateForIndex?.query || ""}
                selectedRows={selectable ? selectedRows : null}
                setSelectedRows={selectable ? setSelectedRows : undefined}
                navigateDetails={navigateDetails}
                ActionsComponent={ActionsComponent}
                renderTags={renderTags}
                dateFilterState={dateFilterState}
                onDateFilterChange={setDateFilterState}
                onDateFilterClear={() => setDateFilterState(DATE_FILTER_FALLBACK_STATE)}
                numericFilterState={numericFilterState}
                onNumericFilterChange={setNumericFilterState}
                onNumericFilterClear={() => setNumericFilterState(NUMERIC_FILTER_FALLBACK_STATE)}
                summaryFilterRequest={summaryFilterRequest}
            />

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                }}
            >

                <HitsPerPage

                    items={[
                        { label: '250', value: 250, },
                        { label: '100', value: 100 },
                        { label: '50', value: 50, default: true },
                    ]}
                />
                <Pagination
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                />
            </div>

        </InstantSearch >



    )
}

export default OrdersInstantSearch
